/**
 * Имена: кабинеты провайдеров и то, что в них видно.
 *
 * Первая часть будущего переезда в один клик. Здесь заводятся ключи от панелей
 * управления доменами — и здесь же видно, живые ли они. Каталог зон, привязка
 * доменов к сайтам и сам день переключения появятся рядом, на своих
 * подвкладках.
 *
 * Про ключ на экране нет ничего, кроме «заполнен». Так и должно быть: секрет,
 * показанный браузеру, — секрет, отданный каждому расширению браузера. Поэтому
 * поле ввода всегда пустое, даже когда ключ давно сохранён, и подписано это
 * словами, а не многоточием, которое можно принять за скрытое значение.
 */
import { useState } from 'react';
import { useQuery } from '../transport/hooks.ts';
import { request } from '../transport/socket.ts';
import { Segmented } from '../components/Segmented.tsx';
import { useParam } from '../router.tsx';
import { Confirm, useConfirm } from '../components/Confirm.tsx';
import { plural } from '../lib/format.ts';
import type { AgentsSnapshot } from '../../shared/protocol/agents.ts';

interface AccountRow {
  id: string;
  provider: 'hetzner';
  label: string;
  hasToken: boolean;
  zones: number | null;
  read: { okAt: string | null; error: string | null };
  write: { okAt: string | null; error: string | null };
  updatedAt: string;
  updatedBy: string;
}

interface ZoneRow {
  id: string;
  name: string;
  ns: string[];
  delegated: string[];
  delegation: string | null;
  delegationCheckedAt: string | null;
  recordCount: number | null;
  ttl: number | null;
  locked: boolean;
}

function when(value: string | null): string {
  if (!value) return 'ни разу';
  return new Date(value).toLocaleString('ru-RU', { dateStyle: 'short', timeStyle: 'short' });
}

/**
 * Состояние ключа — двумя отдельными ответами.
 *
 * «Ключ работает» — недостаточное утверждение: ключ, умеющий только читать,
 * выглядит рабочим ровно до дня переключения. Поэтому чтение и запись стоят
 * рядом и каждое со своей датой.
 */
function Verdict({ label, okAt, error }: {
  label: string; okAt: string | null; error: string | null;
}): React.JSX.Element {
  const state = error ? 'bad' : okAt ? 'ok' : 'unknown';
  return (
    <span className={`dns__verdict dns__verdict--${state}`} title={error ?? undefined}>
      {label}:{' '}
      {state === 'ok' && <b>да</b>}
      {state === 'bad' && <b>нет</b>}
      {state === 'unknown' && <span className="muted">не проверялось</span>}
      {state === 'ok' && <span className="muted"> · {when(okAt)}</span>}
    </span>
  );
}

function Accounts(): React.JSX.Element {
  const { data, loading, reload } = useQuery<{ items: AccountRow[] }>('dns.accounts');
  const [label, setLabel] = useState('');
  const [token, setToken] = useState('');
  const [busy, setBusy] = useState<string | null>(null);
  const [failure, setFailure] = useState<string | null>(null);
  const [zones, setZones] = useState<Record<string, ZoneRow[]>>({});
  const confirm = useConfirm();

  const say = (err: unknown): void => {
    const rpc = (err as { msg?: string }).msg;
    setFailure(rpc ?? (err instanceof Error ? err.message : 'не получилось'));
  };

  async function add(): Promise<void> {
    setBusy('new'); setFailure(null);
    try {
      await request('dns.account.save', { label: label.trim(), token: token.trim() });
      setLabel(''); setToken('');
      reload();
    } catch (err) { say(err); } finally { setBusy(null); }
  }

  async function check(id: string): Promise<void> {
    setBusy(id); setFailure(null);
    try {
      const r = await request<{ zones?: ZoneRow[] }>('dns.account.check', { id });
      if (r.zones) setZones((prev) => ({ ...prev, [id]: r.zones as ZoneRow[] }));
      reload();
    } catch (err) { say(err); } finally { setBusy(null); }
  }

  async function drop(row: AccountRow): Promise<void> {
    setBusy(row.id);
    try {
      await request('dns.account.remove', { id: row.id });
      reload();
    } catch (err) { say(err); } finally { setBusy(null); }
  }

  const askDrop = (row: AccountRow): void => confirm.ask({
    title: `Удалить кабинет «${row.label}»?`,
    consequences: [
      'Ключ будет стёрт: восстановить его отсюда нельзя, только выпустить новый.',
      'Записи в зонах не изменятся — исчезнет только наш доступ к ним.',
    ],
    confirmLabel: 'Удалить',
    run: () => void drop(row),
  });

  if (loading && !data) return <p className="muted">Загружаю кабинеты…</p>;
  const items = data?.items ?? [];

  return (
    <>
      <Confirm
        open={Boolean(confirm.pending)}
        title={confirm.pending?.title ?? ''}
        consequences={confirm.pending?.consequences ?? []}
        confirmLabel={confirm.pending?.confirmLabel ?? 'Подтвердить'}
        busy={busy !== null}
        onConfirm={() => { const run = confirm.pending?.run; confirm.close(); void run?.(); }}
        onCancel={confirm.close}
      />
      {failure && <p className="alert alert--error">{failure}</p>}

      <p className="muted">
        Ключ хранится зашифрованным и на этот экран не возвращается никогда —
        видно только, заполнен он или нет. Проверяется он сразу и дважды:
        перечислением зон и служебной записью <span className="mono">_ebk-probe</span>,
        которая тут же убирается. Вторая проверка нужна потому, что ключ
        «только на чтение» выглядит рабочим ровно до дня переключения.
      </p>

      {items.length === 0 && (
        <p className="muted">Кабинетов пока нет. Заведите первый — форма ниже.</p>
      )}

      {items.length > 0 && (
        <table className="table">
          <thead>
            <tr>
              <th>Кабинет</th><th>Ключ</th><th>Зон</th><th>Что умеет</th><th>Изменён</th><th />
            </tr>
          </thead>
          <tbody>
            {items.map((row) => (
              <tr key={row.id}>
                <td>
                  {row.label}
                  <span className="muted mono"> · {row.provider}</span>
                </td>
                <td>{row.hasToken ? 'заполнен' : <span className="muted">пусто</span>}</td>
                <td>{row.zones ?? '—'}</td>
                <td className="dns__verdicts">
                  <Verdict label="чтение" okAt={row.read.okAt} error={row.read.error} />
                  <Verdict label="запись" okAt={row.write.okAt} error={row.write.error} />
                  {(row.read.error ?? row.write.error) && (
                    <span className="dns__why">{row.read.error ?? row.write.error}</span>
                  )}
                </td>
                <td className="muted">{when(row.updatedAt)}</td>
                <td className="dns__actions">
                  <button type="button" className="button" disabled={busy === row.id}
                          onClick={() => void check(row.id)}>
                    {busy === row.id ? '…' : 'Проверить'}
                  </button>
                  <button type="button" className="button button--danger" disabled={busy === row.id}
                          onClick={() => askDrop(row)}>
                    Удалить
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {items.map((row) => (zones[row.id]?.length ? (
        <div key={`z-${row.id}`} className="dns__zones">
          <h4 className="dns__zones-title">Зоны кабинета «{row.label}»</h4>
          <table className="table">
            <thead>
              <tr><th>Домен</th><th>Записей</th><th>Серверы имён провайдера</th></tr>
            </thead>
            <tbody>
              {zones[row.id]!.map((z) => (
                <tr key={z.id}>
                  <td className="mono">{z.name}</td>
                  <td>{z.recordCount ?? '—'}</td>
                  <td className="mono muted">{z.ns.join(', ') || '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
          <p className="muted">
            Зона в кабинете — ещё не власть над доменом. Если регистратор
            указывает на другие серверы имён, наши правки сохранятся и ничего
            не изменят: самый тихий из возможных отказов. Вердикт провайдера об
            этом — на вкладке «Домены», там же записи и назначение сайтам.
          </p>
        </div>
      ) : null))}

      <div className="dns__form">
        <h4 className="dns__zones-title">Новый кабинет</h4>
        <div className="wizard__row">
          <input className="input" placeholder="как называть — например, Hetzner (домены)"
                 value={label} onChange={(e) => setLabel(e.target.value)} />
          <input className="input wizard__path mono" type="password" autoComplete="new-password"
                 placeholder="ключ доступа к панели провайдера"
                 value={token} onChange={(e) => setToken(e.target.value)} />
          <button type="button" className="button button--primary"
                  disabled={busy === 'new' || label.trim() === '' || token.trim().length < 8}
                  onClick={() => void add()}>
            {busy === 'new' ? 'Проверяю…' : 'Сохранить и проверить'}
          </button>
        </div>
        <p className="muted">
          Для Hetzner ключ выпускается в Hetzner Console. Ключи из <b>старой</b>
          {' '}консоли DNS с нынешним API не работают — если сохранённый ключ
          сразу скажет «недействителен», причина почти наверняка в этом.
        </p>
      </div>
    </>
  );
}

interface ZoneItem extends ZoneRow {
  accountId: string;
  accountLabel: string;
  delegationOk: boolean;
  site: { hostId: string; siteId: string; boundAt: string } | null;
}

interface Records {
  zone: string;
  zoneTtl: number | null;
  addresses: { value: string; names: { name: string; type: string; ttl: number | null; protected: boolean }[] }[];
  mail: { name: string; type: string; ttl: number | null; values: string[] }[];
  other: { name: string; type: string; ttl: number | null; values: string[] }[];
}

/** Время жизни словами: у записи своё или наследуется от зоны. */
function ttlWords(ttl: number | null, zoneTtl: number | null): string {
  if (ttl != null) return `${ttl} с`;
  return zoneTtl != null ? `${zoneTtl} с (зоны)` : 'не задано';
}

/**
 * Записи зоны — сгруппированные по значению.
 *
 * Перед переключением спрашивают не «какие записи есть», а «что указывает на
 * старую машину». По плоскому списку на этот вопрос отвечают глазами и
 * ошибаются: апекс помнят, `www` помнят, а забытый `shop.` десятилетней
 * давности — нет.
 */
function ZoneRecords({ zone }: { zone: ZoneItem }): React.JSX.Element {
  const { data, loading, error } = useQuery<Records>(
    'dns.zone.records', { accountId: zone.accountId, zoneId: zone.id },
  );

  if (loading && !data) return <p className="muted">Читаю записи…</p>;
  if (error) return <p className="alert alert--error">{error.msg}</p>;
  if (!data) return <p className="muted">Записей нет.</p>;

  return (
    <div className="dns__records">
      <table className="table">
        <thead>
          <tr><th>Указывает на</th><th>Кто указывает</th><th>Время жизни</th></tr>
        </thead>
        <tbody>
          {data.addresses.map((g) => (
            <tr key={g.value}>
              <td className="mono">{g.value}</td>
              <td>
                {g.names.map((n) => (
                  <span key={`${n.name}/${n.type}`} className="dns__name">
                    <span className="mono">{n.name}</span>
                    <span className="muted"> {n.type}</span>
                    {n.protected && <span className="tag">защищена</span>}
                  </span>
                ))}
              </td>
              <td className="muted">
                {[...new Set(g.names.map((n) => ttlWords(n.ttl, data.zoneTtl)))].join(', ')}
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      {data.mail.length > 0 && (
        <>
          <h5 className="dns__sub">Почта и подписи</h5>
          <p className="muted">
            Переезд веба их не трогает. Но если здесь перечислен адрес старой
            машины, письма нового магазина молча поедут в спам — мы это
            показываем, чинит человек.
          </p>
          <table className="table">
            <tbody>
              {data.mail.map((m) => (
                <tr key={`${m.name}/${m.type}`}>
                  <td className="mono">{m.name}</td>
                  <td className="muted">{m.type}</td>
                  <td className="mono dns__value">{m.values.join(' · ')}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </>
      )}

      {data.other.length > 0 && (
        <p className="muted">
          Кроме этого в зоне {data.other.length}{' '}
          {plural(data.other.length, 'запись', 'записи', 'записей')} других типов:{' '}
          {[...new Set(data.other.map((o) => o.type))].join(', ')}.
        </p>
      )}
    </div>
  );
}

interface WorldView {
  zone: string;
  assigned: string[];
  delegation: {
    ns: { agree: boolean; values: string[]; answers: { resolver: string; values: string[]; ttl: number | null; error: string | null }[] };
    ours: boolean;
    extra: string[];
    missing: string[];
  };
  addresses: {
    name: string; agree: boolean; values: string[];
    answers: { resolver: string; values: string[]; ttl: number | null; error: string | null }[];
  }[];
}

/**
 * Что о зоне отвечает мир.
 *
 * Панель провайдера говорит, что записано; резолверы — что отдаётся. Между
 * ними TTL, кеши и делегирование, и расхождение означает, что правка
 * сохранилась и ничего не изменила. Спрашиваем двоих независимых: один может
 * держать старое в кеше, и по нему решать «доехало» нельзя.
 */
function World({ zone }: { zone: ZoneItem }): React.JSX.Element {
  const { data, loading, error } = useQuery<WorldView>(
    'dns.zone.resolve', { zone: zone.name, names: ['www'] },
  );

  if (loading && !data) return <p className="muted">Спрашиваю резолверы…</p>;
  if (error) return <p className="alert alert--error">{error.msg}</p>;
  if (!data) return <p className="muted">Ответа нет.</p>;

  const d = data.delegation;
  return (
    <div className="dns__records">
      <p className={d.ours ? 'muted' : 'alert alert--error'}>
        {d.ours
          ? `Делегирование подтверждено снаружи: ${d.ns.values.join(', ')}`
          : `Мир отдаёт другие серверы имён: ${d.ns.values.join(', ') || '—'}`}
        {d.missing.length > 0 && ` · не видно наших: ${d.missing.join(', ')}`}
        {d.extra.length > 0 && ` · лишние: ${d.extra.join(', ')}`}
      </p>
      <table className="table">
        <thead>
          <tr><th>Имя</th><th>Что отдаётся</th><th>Время жизни</th><th /></tr>
        </thead>
        <tbody>
          {data.addresses.map((a) => (
            <tr key={a.name}>
              <td className="mono">{a.name}</td>
              <td className="mono">{a.values.join(', ') || <span className="muted">нет ответа</span>}</td>
              <td className="muted">
                {a.answers.map((x) => (x.ttl != null ? `${x.resolver} ${x.ttl} с` : `${x.resolver} —`)).join(' · ')}
              </td>
              <td>
                {a.agree
                  ? <span className="dns__verdict dns__verdict--ok">согласны</span>
                  : (
                    <span className="dns__verdict dns__verdict--bad"
                          title={a.answers.map((x) => `${x.resolver}: ${x.values.join(',') || x.error || 'пусто'}`).join('\n')}>
                      расходятся
                    </span>
                  )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      <p className="muted">
        Расхождение между резолверами означает, что переключение доезжает прямо
        сейчас: у одного уже новый адрес, у другого ещё старый в кеше. Согласие
        обоих — что доехало. Время жизни рядом показывает, сколько ещё будет
        отдаваться нынешний ответ.
      </p>
    </div>
  );
}

interface ChangeRow {
  _id: string;
  kind: 'ttl' | 'switch';
  zone: string;
  before: { name: string; type: string; ttl: number | null; values: string[] }[];
  after: { name: string; type: string; ttl: number | null; values: string[] }[];
  at: string;
  by: string;
  rolledBackAt: string | null;
}

/**
 * Переключение: понизить время жизни заранее, переставить адрес в час X,
 * вернуть как было.
 *
 * Три действия и один журнал. Журнал здесь не для порядка: откат берёт прежние
 * значения из записи правки, а не из памяти человека — в час X её не хватает.
 */
function Switching(): React.JSX.Element {
  const { data: zonesData } = useQuery<{ zones: ZoneItem[] }>('dns.zones');
  const { data: log, reload } = useQuery<{ items: ChangeRow[] }>('dns.changes');
  const [zone, setZone] = useState('');
  const [names, setNames] = useState('@,www');
  const [ttl, setTtl] = useState(60);
  const [address, setAddress] = useState('');
  const [busy, setBusy] = useState(false);
  const [failure, setFailure] = useState<string | null>(null);
  const [result, setResult] = useState<string | null>(null);
  const confirm = useConfirm();

  const zones = zonesData?.zones ?? [];
  const picked = zones.find((z) => z.name === zone) ?? null;
  const list = names.split(',').map((x) => x.trim()).filter(Boolean);

  const say = (err: unknown): void => {
    setFailure((err as { msg?: string }).msg ?? 'не получилось');
  };

  async function run(what: 'ttl' | 'switch' | 'rollback', changeId?: string): Promise<void> {
    if (!picked && what !== 'rollback') return;
    setBusy(true); setFailure(null); setResult(null);
    try {
      const answer = what === 'rollback'
        ? await request<{ ok: boolean; applied: { name: string; was: string; now: string; ok: boolean }[] }>(
          'dns.change.rollback', { changeId })
        : what === 'ttl'
          ? await request<{ ok: boolean; applied: { name: string; was: string; now: string; ok: boolean }[] }>(
            'dns.ttl.set', {
              accountId: picked!.accountId, zone: picked!.name, ttl,
              names: list.map((n) => ({ name: n, type: 'A' })),
            })
          : await request<{ ok: boolean; applied: { name: string; was: string; now: string; ok: boolean }[] }>(
            'dns.records.switch', {
              accountId: picked!.accountId, zone: picked!.name,
              changes: list.map((n) => ({ name: n, type: 'A', values: [address.trim()] })),
            });
      setResult(
        `${answer.ok ? 'применено' : 'ПРИМЕНИЛОСЬ НЕ ВЕЗДЕ'}: `
        + answer.applied.map((a) => `${a.name} ${a.was} → ${a.now}${a.ok ? '' : ' (не стало)'}`).join('; '),
      );
      reload();
    } catch (err) { say(err); } finally { setBusy(false); }
  }

  return (
    <>
      <Confirm
        open={Boolean(confirm.pending)}
        title={confirm.pending?.title ?? ''}
        consequences={confirm.pending?.consequences ?? []}
        confirmLabel={confirm.pending?.confirmLabel ?? 'Подтвердить'}
        {...(confirm.pending?.typeToConfirm ? { typeToConfirm: confirm.pending.typeToConfirm } : {})}
        busy={busy}
        onConfirm={() => { const r = confirm.pending?.run; confirm.close(); void r?.(); }}
        onCancel={confirm.close}
      />
      {failure && <p className="alert alert--error">{failure}</p>}
      {result && <p className={result.startsWith('применено') ? 'muted' : 'alert alert--error'}>{result}</p>}

      <p className="muted">
        Время жизни понижают <b>заранее</b>: понижение доедет до чужих кешей
        только через старое время. Поставить шестьдесят секунд за минуту до
        переключения — значит получить хвост длиной в прежние два часа и не
        понять, почему. Прежние значения сохраняются до правки, применённое
        перечитывается: провайдер отвечает успехом и на то, чего не сделал.
      </p>

      <div className="dns__form">
        <div className="wizard__row">
          <select className="input" value={zone} onChange={(e) => setZone(e.target.value)}>
            <option value="">— выберите домен —</option>
            {zones.map((z) => (
              <option key={z.id} value={z.name}>
                {z.name}{z.site ? ` · ${z.site.siteId}` : ' · сайту не назначен'}
              </option>
            ))}
          </select>
          <input className="input mono" value={names} onChange={(e) => setNames(e.target.value)}
                 placeholder="имена через запятую: @, www" />
        </div>

        <div className="wizard__row">
          <input className="input" type="number" min={30} max={86400} value={ttl}
                 onChange={(e) => setTtl(Number(e.target.value))} style={{ width: '7rem' }} />
          <button type="button" className="button" disabled={busy || !picked || list.length === 0}
                  onClick={() => confirm.ask({
                    title: `Поставить время жизни ${ttl} с в зоне ${zone}?`,
                    consequences: [
                      `Затронет: ${list.join(', ')} (тип A).`,
                      'Прежние значения сохранятся — вернуть можно одной кнопкой.',
                      'Понижение доедет до кешей через СТАРОЕ время жизни, а не сразу.',
                    ],
                    confirmLabel: 'Поставить',
                    run: () => void run('ttl'),
                  })}>
            Поставить время жизни
          </button>
        </div>

        <div className="wizard__row">
          <input className="input mono" value={address} onChange={(e) => setAddress(e.target.value)}
                 placeholder="новый адрес, например 95.217.116.80" />
          <button type="button" className="button button--danger"
                  disabled={busy || !picked || !address.trim() || list.length === 0}
                  onClick={() => confirm.ask({
                    title: `Переключить ${zone} на ${address.trim()}?`,
                    consequences: [
                      `Имена ${list.join(', ')} начнут указывать на ${address.trim()}.`,
                      'Посетители пойдут на новую машину по мере истечения времени жизни записей.',
                      'Прежние адреса сохранятся: откат — одной кнопкой из журнала ниже.',
                    ],
                    confirmLabel: 'Переключить',
                    typeToConfirm: zone,
                    run: () => void run('switch'),
                  })}>
            Переключить
          </button>
        </div>
      </div>

      <h4 className="dns__zones-title">Что меняли</h4>
      {(log?.items ?? []).length === 0 && <p className="muted">Правок ещё не было.</p>}
      {(log?.items ?? []).length > 0 && (
        <table className="table">
          <thead>
            <tr><th>Когда</th><th>Что</th><th>Было</th><th>Стало</th><th /></tr>
          </thead>
          <tbody>
            {(log?.items ?? []).map((c) => (
              <tr key={c._id} className={c.rolledBackAt ? 'row--revoked' : undefined}>
                <td className="muted">{when(c.at)}</td>
                <td>{c.kind === 'ttl' ? 'время жизни' : 'переключение'}<span className="muted"> · {c.zone}</span></td>
                <td className="mono dns__value">
                  {c.before.map((b) => `${b.name} ${c.kind === 'ttl' ? `${b.ttl ?? 'по зоне'} с` : b.values.join(',')}`).join('; ')}
                </td>
                <td className="mono dns__value">
                  {c.after.map((a) => `${a.name} ${c.kind === 'ttl' ? `${a.ttl} с` : a.values.join(',')}`).join('; ')}
                </td>
                <td>
                  {c.rolledBackAt
                    ? <span className="muted">откачено {when(c.rolledBackAt)}</span>
                    : (
                      <button type="button" className="button" disabled={busy}
                              onClick={() => confirm.ask({
                                title: 'Вернуть как было?',
                                consequences: [
                                  'Значения возьмутся из записи правки, а не из памяти.',
                                  'Применённое будет перечитано и сверено.',
                                ],
                                confirmLabel: 'Вернуть',
                                run: () => void run('rollback', c._id),
                              })}>
                        Вернуть как было
                      </button>
                    )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </>
  );
}

/** Каталог доменов и привязка их к сайтам. */
function Catalog(): React.JSX.Element {
  const { data, loading, error, reload } = useQuery<{
    zones: ZoneItem[];
    failed: { accountId: string; label: string; error: string }[];
  }>('dns.zones');
  const { data: agents } = useQuery<AgentsSnapshot>('agents.list');
  const [open, setOpen] = useState<string | null>(null);
  const [world, setWorld] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [failure, setFailure] = useState<string | null>(null);

  const sites = (agents?.agents ?? []).flatMap((a) =>
    (a.report?.sites ?? []).map((s) => ({ hostId: a.hostId, siteId: s.id, label: a.label })));

  async function bind(zone: ZoneItem, value: string): Promise<void> {
    setBusy(zone.name); setFailure(null);
    try {
      if (!value) {
        await request('dns.domain.unbind', { zone: zone.name });
      } else {
        const [hostId, siteId] = value.split('|');
        await request('dns.domain.bind', {
          zone: zone.name, accountId: zone.accountId, hostId, siteId,
        });
      }
      reload();
    } catch (err) {
      setFailure((err as { msg?: string }).msg ?? 'не получилось');
    } finally { setBusy(null); }
  }

  if (loading && !data) return <p className="muted">Спрашиваю кабинеты…</p>;
  if (error) return <p className="alert alert--error">{error.msg}</p>;

  const zones = data?.zones ?? [];

  return (
    <>
      {failure && <p className="alert alert--error">{failure}</p>}
      {(data?.failed ?? []).map((f) => (
        <p key={f.accountId} className="alert alert--error">
          Кабинет «{f.label}» не ответил: {f.error}. Домены из остальных
          кабинетов показаны — одна недоступная панель не должна прятать всё.
        </p>
      ))}

      {zones.length === 0 && (
        <p className="muted">
          Ни одной зоны не видно. Проверьте ключ на соседней вкладке.
        </p>
      )}

      {zones.length > 0 && (
        <table className="table">
          <thead>
            <tr>
              <th>Домен</th><th>Делегирование</th><th>Записей</th>
              <th>Чей сайт</th><th>Кабинет</th><th />
            </tr>
          </thead>
          <tbody>
            {zones.map((z) => (
              <tr key={`${z.accountId}-${z.id}`}>
                <td className="mono">{z.name}</td>
                <td>
                  {z.delegationOk ? (
                    <span className="dns__verdict dns__verdict--ok"><b>наше</b></span>
                  ) : (
                    <span className="dns__verdict dns__verdict--bad" title={z.delegated.join(', ')}>
                      <b>{z.delegation ?? 'неизвестно'}</b>
                    </span>
                  )}
                  {z.delegationCheckedAt && (
                    <span className="muted"> · проверено {when(z.delegationCheckedAt)}</span>
                  )}
                </td>
                <td>{z.recordCount ?? '—'}</td>
                <td>
                  <select
                    className="input input--sm"
                    disabled={busy === z.name}
                    value={z.site ? `${z.site.hostId}|${z.site.siteId}` : ''}
                    onChange={(e) => void bind(z, e.target.value)}
                  >
                    <option value="">— не назначен —</option>
                    {sites.map((s) => (
                      <option key={`${s.hostId}|${s.siteId}`} value={`${s.hostId}|${s.siteId}`}>
                        {s.siteId} · {s.label}
                      </option>
                    ))}
                  </select>
                </td>
                <td className="muted">{z.accountLabel}</td>
                <td>
                  <button type="button" className="button"
                          onClick={() => setOpen(open === z.id ? null : z.id)}>
                    {open === z.id ? 'Свернуть' : 'Записи'}
                  </button>
                  {' '}
                  <button type="button" className="button"
                          onClick={() => setWorld(world === z.id ? null : z.id)}>
                    {world === z.id ? 'Свернуть' : 'Что видит мир'}
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {zones.filter((z) => z.id === world).map((z) => (
        <div key={`w-${z.id}`} className="dns__zones">
          <h4 className="dns__zones-title">Что мир отвечает про {z.name}</h4>
          <World zone={z} />
        </div>
      ))}

      {zones.filter((z) => z.id === open).map((z) => (
        <div key={`r-${z.id}`} className="dns__zones">
          <h4 className="dns__zones-title">Записи зоны {z.name}</h4>
          <ZoneRecords zone={z} />
        </div>
      ))}

      <p className="muted">
        «Делегирование» — вердикт самого провайдера: совпадают ли серверы имён,
        назначенные здесь, с теми, куда смотрит регистратор. Если не совпадают,
        наши правки сохранятся и <b>ничего не изменят</b> — это самый тихий из
        возможных отказов, и в день переключения он стоил бы магазина.
      </p>
    </>
  );
}

export function Dns(): React.JSX.Element {
  const [tab, setTab] = useParam(
    'section', 'accounts', ['accounts', 'catalog', 'switching'] as const,
  );

  return (
    <section className="panel panel--fill">
      <header className="panel__head">
        <h2 className="panel__title">Имена и домены</h2>
      </header>

      <div className="panel__tabs">
        <Segmented
          ariaLabel="разделы имён"
          active={tab}
          onSelect={(id) => setTab(id as typeof tab)}
          items={[
            { id: 'accounts', label: 'Кабинеты и ключи' },
            { id: 'catalog', label: 'Домены' },
            { id: 'switching', label: 'Переключение' },
          ]}
        />
      </div>

      <div className="panel__scroll">
        {tab === 'accounts' && <Accounts />}
        {tab === 'catalog' && <Catalog />}
        {tab === 'switching' && <Switching />}
      </div>
    </section>
  );
}
